import React, { useMemo } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { ArrowBackOutline, ArrowLeftOutlineLegacy, ArrowRightFill } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Button, DeprecatedIconButton, LinkButton, Stack } from '@lyearn/ui';
import Breadcrumbs from '@/components/Breadcrumbs';
import useQueryParam, { StringParam } from '@/hooks/useQueryParam';
import { useUpdateContent } from '@/modules/corporate/modules/course/views/CourseViewer/components/MainContent/LessonMainContent/hooks/useUpdateContent';
import { PARENT_ENTITY_ID } from '@/modules/corporate/modules/exercise/views/Details/utils';
import { PerformanceStatusEnum } from '@/types/schema';

import { QuizHeaderProps } from './types';

function QuizHeader(props: QuizHeaderProps) {
  const { quizId, label, isPreviewing, onClose, canRetakeQuiz, onRetakeQuiz, quizPerformance } =
    props;
  const { t } = useTranslation();
  const { isMobile } = useScreen();

  const [parentEntityId] = useQueryParam(PARENT_ENTITY_ID, StringParam);

  const BackIcon = isMobile ? ArrowLeftOutlineLegacy : ArrowBackOutline;
  const ActionButton = !isMobile ? LinkButton : DeprecatedIconButton;
  const modifiedQuizPerformanceStatus = useMemo(() => {
    return quizPerformance?.isSubmitted
      ? PerformanceStatusEnum.Completed
      : PerformanceStatusEnum.InProgress;
  }, [quizPerformance]);
  const { onNext } = useUpdateContent(parentEntityId ?? '', quizId, modifiedQuizPerformanceStatus);

  return (
    <Stack
      alignItems="center"
      className={cn(
        'shrink-0 border-b border-divider bg-bg-primary px-24 ps-8',
        isMobile ? 'px-20 pb-8 ps-8' : 'sticky top-0 z-10 h-60 start-0',
      )}
      gapX={8}
      justifyContent="between">
      <Stack alignItems="center" justifyContent="between">
        {isPreviewing ? null : (
          <Stack alignItems="center" gapX={8}>
            <Breadcrumbs
              breadcrumbs={[{ label: label ?? '' }]}
              className="p-0"
              onBackClick={onClose}
            />
          </Stack>
        )}
      </Stack>
      <Stack
        className={cn({
          'fixed bottom-0 bg-white px-16 py-36 z-50 w-full border-t border-border': isMobile,
        })}
        direction={isMobile ? 'col-reverse' : 'row'}
        gapX={8}
        gapY={12}
        justifyContent="center">
        {canRetakeQuiz ? (
          <Button size={isMobile ? 'large' : 'medium'} variant="outlined" onClick={onRetakeQuiz}>
            {t('QuizPlayer:ReAttempt')}
          </Button>
        ) : (
          <Button size={isMobile ? 'large' : 'medium'} variant="outlined" onClick={onClose}>
            {t('QuizPlayer:BackToQuiz')}
          </Button>
        )}
        {parentEntityId && onNext && (
          <Button
            EndIcon={ArrowRightFill}
            fullWidth={isMobile}
            size={isMobile ? 'large' : 'medium'}
            variant="filled"
            onClick={onNext}>
            {t('Common:NextContent')}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

export default QuizHeader;
